import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { IApiSmpGptAgent } from "@smp/api";
import { UiButton, UiTypography } from "@smp/ui";
import { Formik, FormikConfig } from "formik";
import { useSnackbar } from "notistack";
import { FC, useCallback, useMemo } from "react";
import * as Yup from "yup";

import { FormTextFieldV2 } from "@/components/form/form_text_field_v2";
import { useFailureTranslator } from "@/hooks/use_failure_translator";
import { useMutationGptAgentCreate, useMutationGptAgentPatch } from "@/queries";
import { dialogAdvisorConfig } from "@/screens/app/components/dialogs/dialog_advisor";
import { AdvisorAccordion } from "@/screens/app/components/dialogs/dialog_advisor/components/advisor_accordion";
import { BtnRemoveAgent } from "@/screens/app/components/dialogs/dialog_advisor/components/btn_remove_agent";
import { INITIAL_AGENT_PARAMS_POST } from "@/screens/app/components/dialogs/dialog_advisor/fixtures/agent_params_post";
import { generateAgentContext } from "@/screens/app/components/dialogs/dialog_advisor/helpers/generate_agent_context";
import { metrikaCombinedGoal } from "@/utils";

import { ExtraParams } from "../_extra/extra_params";
import { PostParams } from "../_extra/post_params";
import classes from "./agent_edit.module.css";
import { GeneralParams } from "./screens/general_params";

export interface IAgentEditProps {
  agent: IApiSmpGptAgent;
  agentIdx: number;
  onDelete: () => void;
  onSuccess: () => void;
  onDuplicate: (callback: () => void) => void;
  onUpdate: (agent: IApiSmpGptAgent) => void;
}

export const AgentEdit: FC<IAgentEditProps> = ({
  agent,
  agentIdx,
  onDelete,
  onSuccess,
  onDuplicate,
  onUpdate,
}) => {
  const { i18n } = useLingui();
  const { enqueueSnackbar } = useSnackbar();
  const { failureTranslationGet } = useFailureTranslator();

  const { mutateAsync: patchAgent } = useMutationGptAgentPatch({
    onSuccess: () => {
      enqueueSnackbar(<Trans>Preset successfully updated</Trans>, {
        variant: "success",
      });

      onSuccess();
      onUpdate(agent);

      metrikaCombinedGoal("gpt_agent_update", { advisor: 1 });
    },
    onError: (e) => {
      enqueueSnackbar(failureTranslationGet(e), {
        variant: "error",
      });
    },
  });

  const { mutate: createAgent, isLoading: isAgentCreating } =
    useMutationGptAgentCreate({
      onSuccess: () => {
        enqueueSnackbar(<Trans>Preset successfully duplicated</Trans>, {
          variant: "success",
        });

        onSuccess();

        metrikaCombinedGoal("gpt_agent_create", { advisor: 1 });
      },
      onError: (e) => {
        enqueueSnackbar(failureTranslationGet(e), {
          variant: "error",
        });
      },
    });

  const initialValues = useMemo<NonNullable<IApiSmpGptAgent["params"]>>(
    () => ({
      context: agent.params?.context,
      post: {
        ...INITIAL_AGENT_PARAMS_POST,
        language: i18n.locale,
        ...agent.params?.post,
      },
    }),
    [agent.params?.context, agent.params?.post, i18n.locale],
  );

  const validationSchema = useMemo<
    NonNullable<FormikConfig<typeof initialValues>["validationSchema"]>
  >(
    () =>
      Yup.object().shape({
        context: Yup.object().shape(
          [
            "niche",
            "occupation",
            "target_audience",
            "problem",
            "usp",
            "benefits",
            "goal",
            "key_messages",
            "avoided_topics",
            "sample",
          ].reduce<Record<string, Yup.AnySchema>>((acc, prop) => {
            acc[prop] = Yup.string().max(
              dialogAdvisorConfig.ask_input_max_length,
              t`Max. length is ${dialogAdvisorConfig.ask_input_max_length} characters`,
            );

            return acc;
          }, {}),
        ),
      }),
    [],
  );

  const handleSubmit = useCallback<
    NonNullable<FormikConfig<typeof initialValues>["onSubmit"]>
  >(
    async (values) => {
      await patchAgent({
        id: agent.id,
        context: generateAgentContext(values),
        params: {
          socialPageGroupId: agent.params?.socialPageGroupId,
          ...values,
        },
      });
    },
    [agent.id, agent.params?.socialPageGroupId, patchAgent],
  );

  const handleDuplicate = useCallback(
    () =>
      onDuplicate(() =>
        createAgent({
          context: agent.context,
          params: {
            ...agent.params,
            context: {
              ...agent.params?.context,
              niche: agent.params?.context?.niche + " - " + t`preset copy`,
            },
          },
        }),
      ),
    [agent.context, agent.params, createAgent, onDuplicate],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      validateOnChange
      validateOnBlur
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, dirty, isSubmitting }) => (
        <form onSubmit={handleSubmit}>
          <div className={classes.form}>
            <div className={classes.form__row}>
              <UiTypography variant="h5">
                <Trans>Preset name</Trans>
              </UiTypography>

              <div>
                <FormTextFieldV2
                  fullWidth
                  multiline
                  variant="outlined"
                  name="context.niche"
                />
              </div>
            </div>

            <UiTypography variant="h4">
              <Trans>Core parameters</Trans>
            </UiTypography>

            <GeneralParams
              classes={{
                group: classes.form__group,
                row: classes.form__row,
              }}
            />

            <AdvisorAccordion title={<Trans>Additional parameters</Trans>}>
              <ExtraParams
                classes={{
                  group: classes.form__group,
                  row: classes.form__row,
                }}
              />
            </AdvisorAccordion>

            <AdvisorAccordion title={<Trans>Posts settings</Trans>}>
              <PostParams
                classes={{
                  group: classes.form__group,
                  row: classes.form__row,
                  row_col: classes.form__row_col,
                  row__col: classes.form__row__col,
                  radio: classes.form__radio,
                }}
              />
            </AdvisorAccordion>
          </div>

          <div className={classes.buttons}>
            <BtnRemoveAgent
              agentId={agent.id}
              agentIdx={agentIdx}
              onDelete={onDelete}
            >
              {({ fire, isLoading: isDeleting }) => (
                <>
                  <UiButton
                    color="secondary"
                    variant="outfill"
                    size="large"
                    onClick={fire}
                    isLoading={isDeleting}
                    disabled={isAgentCreating || isSubmitting}
                  >
                    <Trans>Delete preset</Trans>
                  </UiButton>

                  <div className={classes.buttons__sep} />

                  <UiButton
                    variant="outlined"
                    size="large"
                    disabled={isDeleting || isSubmitting}
                    isLoading={isAgentCreating}
                    onClick={handleDuplicate}
                  >
                    <Trans>Duplicate preset</Trans>
                  </UiButton>

                  <UiButton
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={!dirty || isDeleting || isAgentCreating}
                    isLoading={isSubmitting}
                  >
                    <Trans>Save changes</Trans>
                  </UiButton>
                </>
              )}
            </BtnRemoveAgent>
          </div>
        </form>
      )}
    </Formik>
  );
};
